import { forwardRef, useState, useCallback, useRef, FocusEvent } from "react";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { useId } from "../../hooks/useId";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { RadioGroupProps } from "./RadioGroup";
import { SharedRadio } from "../Radio/SharedRadio";
import { useValidity, ValidityGroup } from "../ValidityGroup";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { GroupLayoutWrapper, InputContainer, NoPaddingFormComponents } from "../shared";
import { getOptions } from "./helpers";
import styles from "./styles.module.scss";
import { Placeholder } from "./Placeholder";

/**
 * The `Input` component properties.
 */
export interface InputProps extends RadioGroupProps, NoPaddingFormComponents {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * A form input that presents a group of series of checkboxes which can be toggled on and off.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    required = false,
    label,
    tooltip,
    instructions,
    orientation = "vertical",
    children,
    autoFocus = false,
    defaultValue = null,
    unpadded = false,
    hideInstructions,
    hideLabel,
    onFocusEnter,
    onFocusExit,
    onChange,
  } = props;
  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.RadioGroup, "radio-group"],
    testIdPrefix: "radio-group",
  });

  const id = useId();
  const groupDisabled = useInputDisabled(props);
  const groupReadOnly = useInputReadonly(props);
  const [invalid, error] = useValidity();

  const options = getOptions(children);

  const [focused, setFocused] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const changeHandler = useCallback((newValue: string | number | boolean, checked: boolean | null) => {
    if (!checked) return;

    onChange?.(newValue, value);
  }, [value, onChange]);

  const focusHandler = useCallback(() => {
    if (!focused) {
      setFocused(true);
      onFocusEnter?.();
    }
  }, [focused, onFocusEnter]);

  const blurHandler = useCallback(
    (ev: FocusEvent) => {
      const { relatedTarget } = ev;
      const { current: root } = rootRef;

      if (relatedTarget && root && isDescendentOf(root, relatedTarget)) return;

      if (focused) {
        setFocused(false);
        onFocusExit?.();
      }
    },
    [focused, onFocusExit],
  );

  // Generate error message ID if there's an error
  const errorMessageId = invalid && error ? `${id}-error` : undefined;

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} optionCount={options.length || 2} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        disabled={groupDisabled}
        readOnly={groupReadOnly}
        tooltip={tooltip}
        required={required}
        instructions={instructions}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <div
          className="group-container"
          ref={rootRef}
          role="radiogroup"
          aria-labelledby={`${id}-label`}
          aria-describedby={instructions ? `${id}-instructions` : undefined}
          aria-required={required ? "true" : undefined}
          aria-invalid={invalid ? "true" : undefined}
          aria-errormessage={errorMessageId}
        >
          <ValidityGroup invalid={invalid}>
            <GroupLayoutWrapper orientation={orientation} unpadded={unpadded}>
              {options.map((option, index) => {
                const { value: checkValue, disabled = false, children } = option;

                return (
                  <SharedRadio
                    ref={index === 0 ? ref : undefined}
                    id={`${id}-${index}`}
                    className="item"
                    autoFocus={index === 0 && autoFocus === true}
                    key={`${checkValue}`}
                    value={checkValue === value}
                    label={children}
                    disabled={disabled || groupDisabled}
                    unpadded={unpadded}
                    readOnly={groupReadOnly}
                    onFocus={focusHandler}
                    onBlur={blurHandler}
                    onChange={(checked) => {
                      changeHandler(checkValue, checked);
                    }}
                  />
                );
              })}
            </GroupLayoutWrapper>
          </ValidityGroup>
        </div>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
